/**
 * Theme Detail View
 * The "magic" view where consultants see holistic discovery in action
 * - All CIs in a theme side-by-side
 * - Domain-level questions answered once
 * - Cross-CI answer reuse opportunities
 * - Build readiness progression
 */

import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, 
  Circle, 
  TrendingUp, 
  Lightbulb, 
  Plus, 
  ExternalLink,
  AlertCircle,
  Copy,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Loader2,
  FileQuestion,
  Link2
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { AIExtractionModal } from './AIExtractionModal';

interface ThemeQuestion {
  id: string;
  question: string;
  answerType: string;
  category: string;
  priority: 'critical' | 'important' | 'optional';
  appliesTo: 'all' | 'global' | 'space-specific';
  answer?: any;
  answeredBy?: string; // CI name that answered it
  canReuseTo?: string[]; // CI IDs that can reuse this answer
}

interface ConfigItemSummary {
  id: string;
  name: string;
  status: 'not-started' | 'discovery' | 'ready-to-build' | 'building' | 'built';
  buildReadiness: number;
  isGlobal: boolean;
  answeredQuestions: number;
  totalQuestions: number;
  missingCritical: string[];
  reuseOpportunities: Array<{
    fromCI: string;
    questionId: string;
    question: string;
    answer: any;
  }>;
}

interface ThemeDetailData {
  themeId: string;
  themeName: string;
  themeDescription: string;
  phase: string;
  status: 'not-started' | 'in-progress' | 'complete';
  overallProgress: number;
  domainQuestions: ThemeQuestion[];
  configItems: ConfigItemSummary[];
  insights: Array<{
    id: string;
    type: 'pattern' | 'opportunity' | 'risk' | 'recommendation';
    message: string;
    actionable: boolean;
    affectedCIs: string[];
  }>;
  dependencies: string[];
  blockedBy: string[];
}

export function ThemeDetailView({
  themeId,
  companyId,
  accessToken,
  onBack
}: {
  themeId: string;
  companyId: string;
  accessToken: string;
  onBack: () => void;
}) {
  const [data, setData] = useState<ThemeDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    domain: true,
    cis: true,
    insights: true,
  });
  const [selectedCI, setSelectedCI] = useState<string | null>(null);
  const [applyingAnswer, setApplyingAnswer] = useState(false);
  const [extractionModalOpen, setExtractionModalOpen] = useState(false);
  const [selectedCIForExtraction, setSelectedCIForExtraction] = useState<ConfigItemSummary | null>(null);

  useEffect(() => {
    loadThemeDetail();
  }, [themeId, companyId]);

  const loadThemeDetail = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery-context/${companyId}/theme/${themeId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load theme details');
      }

      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err: any) {
      console.error('Error loading theme detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyAnswerToCI = async (questionId: string, targetCIId: string, answer: any) => {
    try {
      setApplyingAnswer(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery-context/${companyId}/apply-answer`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            questionId,
            targetCIId,
            answer,
            themeId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to apply answer');
      }

      // Reload to show updated state
      await loadThemeDetail();
    } catch (err: any) {
      console.error('Error applying answer:', err);
      alert(`Failed to apply answer: ${err.message}`);
    } finally {
      setApplyingAnswer(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="size-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Failed to load theme details: {error}</p>
        <button onClick={onBack} className="mt-4 text-blue-600 hover:underline">
          ← Back to themes
        </button>
      </div>
    );
  }

  const readyCIs = data.configItems.filter(ci => ci.status === 'ready-to-build');
  const inProgressCIs = data.configItems.filter(ci => ci.status === 'discovery');
  const notStartedCIs = data.configItems.filter(ci => ci.status === 'not-started');

  const criticalDomainQuestions = data.domainQuestions.filter(q => q.priority === 'critical');
  const answeredDomainQuestions = data.domainQuestions.filter(q => q.answer);
  const unansweredCritical = criticalDomainQuestions.filter(q => !q.answer);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button 
          onClick={onBack}
          className="text-blue-600 hover:underline mb-2 flex items-center gap-1"
        >
          ← Back to overview
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-gray-900">{data.themeName}</h2>
            <p className="text-gray-600 mt-1">{data.themeDescription}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                {data.phase}
              </span>
              {data.status === 'complete' && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded flex items-center gap-1">
                  <CheckCircle2 className="size-3" />
                  Complete
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-gray-900 text-3xl">{Math.round(data.overallProgress)}%</div>
            <div className="text-sm text-gray-600">Overall Progress</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
            style={{ width: `${data.overallProgress}%` }}
          />
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600">Total CIs</div>
          <div className="text-gray-900 text-2xl mt-1">{data.configItems.length}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-sm text-green-900">Ready to Build</div>
          <div className="text-green-900 text-2xl mt-1">{readyCIs.length}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-blue-900">In Progress</div>
          <div className="text-blue-900 text-2xl mt-1">{inProgressCIs.length}</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="text-sm text-purple-900">Domain Questions</div>
          <div className="text-purple-900 text-2xl mt-1">
            {answeredDomainQuestions.length}/{data.domainQuestions.length}
          </div>
        </div>
      </div>

      {/* Critical Alert */}
      {unansweredCritical.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="size-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-orange-900">
              {unansweredCritical.length} critical domain {unansweredCritical.length === 1 ? 'question' : 'questions'} unanswered
            </div>
            <div className="text-sm text-orange-700 mt-1">
              These questions apply to all CIs in this theme. Answer them once to accelerate discovery.
            </div>
          </div>
        </div>
      )}

      {/* Domain-Level Questions Section */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <button
          onClick={() => toggleSection('domain')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            {expandedSections.domain ? (
              <ChevronDown className="size-5 text-gray-400" />
            ) : (
              <ChevronRight className="size-5 text-gray-400" />
            )}
            <FileQuestion className="size-5 text-purple-600" />
            <div className="text-left">
              <div className="text-gray-900">Domain-Level Questions</div>
              <div className="text-sm text-gray-600">
                Answer once, apply to {data.configItems.length} CIs • {answeredDomainQuestions.length}/{data.domainQuestions.length} answered
              </div>
            </div>
          </div>
          {unansweredCritical.length > 0 && (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
              {unansweredCritical.length} critical
            </span>
          )}
        </button>

        {expandedSections.domain && (
          <div className="border-t border-gray-200 p-4 space-y-3">
            {data.domainQuestions.length === 0 ? (
              <p className="text-gray-600 text-sm">No domain questions defined for this theme.</p>
            ) : (
              data.domainQuestions.map(q => (
                <div 
                  key={q.id}
                  className={`border rounded-lg p-4 ${
                    q.answer 
                      ? 'bg-green-50 border-green-200' 
                      : q.priority === 'critical'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {q.answer ? (
                          <CheckCircle2 className="size-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <Circle className="size-4 text-gray-400 flex-shrink-0" />
                        )}
                        <div className="text-gray-900">{q.question}</div>
                      </div>
                      <div className="ml-6 mt-1 flex items-center gap-2 text-xs text-gray-600">
                        <span className={`px-1.5 py-0.5 rounded ${
                          q.priority === 'critical' ? 'bg-orange-100 text-orange-800' :
                          q.priority === 'important' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {q.priority}
                        </span>
                        <span>→ Applies to: {q.appliesTo === 'all' ? 'All CIs' : q.appliesTo}</span>
                      </div>
                      {q.answer && (
                        <div className="ml-6 mt-2 p-2 bg-white border border-green-200 rounded text-sm">
                          <div className="text-gray-600 text-xs mb-1">Answer (from {q.answeredBy}):</div>
                          <div className="text-gray-900">
                            {typeof q.answer === 'string' ? q.answer : JSON.stringify(q.answer, null, 2)}
                          </div>
                          {q.canReuseTo && q.canReuseTo.length > 0 && (
                            <div className="mt-2 text-xs text-green-700">
                              ✓ Applied to {q.canReuseTo.length} other {q.canReuseTo.length === 1 ? 'CI' : 'CIs'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Config Items Section */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <button
          onClick={() => toggleSection('cis')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            {expandedSections.cis ? (
              <ChevronDown className="size-5 text-gray-400" />
            ) : (
              <ChevronRight className="size-5 text-gray-400" />
            )}
            <div className="text-left">
              <div className="text-gray-900">Config Items</div>
              <div className="text-sm text-gray-600">
                {readyCIs.length} ready • {inProgressCIs.length} in progress • {notStartedCIs.length} not started
              </div>
            </div>
          </div>
        </button>

        {expandedSections.cis && (
          <div className="border-t border-gray-200 divide-y divide-gray-200">
            {/* Ready to Build */}
            {readyCIs.length > 0 && (
              <div className="p-4">
                <h4 className="text-green-900 mb-3">Ready to Build ({readyCIs.length})</h4>
                <div className="space-y-2">
                  {readyCIs.map(ci => (
                    <ConfigItemCard
                      key={ci.id}
                      ci={ci}
                      onApplyAnswer={applyAnswerToCI}
                      applying={applyingAnswer}
                      onExtract={setExtractionModalOpen}
                      onExtractCI={setSelectedCIForExtraction}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* In Progress */}
            {inProgressCIs.length > 0 && (
              <div className="p-4">
                <h4 className="text-blue-900 mb-3">In Progress ({inProgressCIs.length})</h4>
                <div className="space-y-2">
                  {inProgressCIs.map(ci => (
                    <ConfigItemCard
                      key={ci.id}
                      ci={ci}
                      onApplyAnswer={applyAnswerToCI}
                      applying={applyingAnswer}
                      onExtract={setExtractionModalOpen}
                      onExtractCI={setSelectedCIForExtraction}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Not Started */}
            {notStartedCIs.length > 0 && (
              <div className="p-4">
                <h4 className="text-gray-700 mb-3">Not Started ({notStartedCIs.length})</h4>
                <div className="space-y-2">
                  {notStartedCIs.map(ci => (
                    <ConfigItemCard
                      key={ci.id}
                      ci={ci}
                      onApplyAnswer={applyAnswerToCI}
                      applying={applyingAnswer}
                      onExtract={setExtractionModalOpen}
                      onExtractCI={setSelectedCIForExtraction}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Insights Section */}
      {data.insights.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('insights')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSections.insights ? (
                <ChevronDown className="size-5 text-gray-400" />
              ) : (
                <ChevronRight className="size-5 text-gray-400" />
              )}
              <Lightbulb className="size-5 text-purple-600" />
              <div className="text-left">
                <div className="text-gray-900">AI Insights</div>
                <div className="text-sm text-gray-600">
                  {data.insights.length} pattern{data.insights.length !== 1 ? 's' : ''} detected
                </div>
              </div>
            </div>
          </button>

          {expandedSections.insights && (
            <div className="border-t border-gray-200 p-4 space-y-3">
              {data.insights.map(insight => (
                <div
                  key={insight.id}
                  className={`border rounded-lg p-4 ${
                    insight.type === 'risk' 
                      ? 'bg-red-50 border-red-200'
                      : insight.type === 'opportunity'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-purple-50 border-purple-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <Sparkles className="size-4 flex-shrink-0 mt-0.5 text-purple-600" />
                    <div className="flex-1">
                      <div className="text-gray-900">{insight.message}</div>
                      {insight.affectedCIs.length > 0 && (
                        <div className="text-xs text-gray-600 mt-1">
                          Affects: {insight.affectedCIs.join(', ')}
                        </div>
                      )}
                      {insight.actionable && (
                        <div className="mt-2">
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                            Action Available
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dependencies & Blockers */}
      {(data.dependencies.length > 0 || data.blockedBy.length > 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-yellow-900 mb-2">Dependencies</h4>
          {data.blockedBy.length > 0 && (
            <div className="text-sm text-yellow-800 mb-2">
               Blocked by: {data.blockedBy.join(', ')}
            </div>
          )}
          {data.dependencies.length > 0 && (
            <div className="text-sm text-yellow-800">
               Depends on: {data.dependencies.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* AI Extraction Modal */}
      {selectedCIForExtraction && (
        <AIExtractionModal
          isOpen={extractionModalOpen}
          onClose={() => {
            setExtractionModalOpen(false);
            setSelectedCIForExtraction(null);
          }}
          companyId={companyId}
          themeId={themeId}
          themeName={data.themeName}
          ciId={selectedCIForExtraction.id}
          ciName={selectedCIForExtraction.name}
          accessToken={accessToken}
          onSuccess={loadThemeDetail}
        />
      )}
    </div>
  );
}

// Config Item Card Component
function ConfigItemCard({
  ci,
  onApplyAnswer,
  applying,
  onExtract,
  onExtractCI
}: {
  ci: ConfigItemSummary;
  onApplyAnswer: (questionId: string, targetCIId: string, answer: any) => Promise<void>;
  applying: boolean;
  onExtract: (open: boolean) => void;
  onExtractCI: (ci: ConfigItemSummary | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready-to-build':
        return 'text-green-600 bg-green-100';
      case 'discovery':
        return 'text-blue-600 bg-blue-100';
      case 'building':
        return 'text-purple-600 bg-purple-100';
      case 'built':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-400 bg-gray-100';
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="size-4 text-gray-400" />
            ) : (
              <ChevronRight className="size-4 text-gray-400" />
            )}
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{ci.name}</span>
                {ci.isGlobal && <span className="text-xs text-blue-600">Global</span>}
              </div>
              <div className="text-xs text-gray-600">
                {ci.answeredQuestions}/{ci.totalQuestions} questions answered
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {ci.reuseOpportunities.length > 0 && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded flex items-center gap-1">
              <Copy className="size-3" />
              {ci.reuseOpportunities.length}
            </span>
          )}
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-sm text-gray-900">{ci.buildReadiness}%</div>
            </div>
            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full ${
                  ci.buildReadiness >= 80 ? 'bg-green-500' :
                  ci.buildReadiness >= 50 ? 'bg-blue-500' :
                  'bg-gray-400'
                }`}
                style={{ width: `${ci.buildReadiness}%` }}
              />
            </div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 p-3 bg-gray-50 space-y-3">
          {/* AI Extraction Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExtractCI(ci);
              onExtract(true);
            }}
            className="w-full px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
          >
            <Sparkles className="size-4" />
            Extract from Meeting Notes with AI
          </button>

          {/* Missing Critical Info */}
          {ci.missingCritical.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded p-2">
              <div className="text-xs text-orange-900 mb-1">Missing Critical:</div>
              <ul className="text-xs text-orange-800 list-disc list-inside">
                {ci.missingCritical.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Reuse Opportunities */}
          {ci.reuseOpportunities.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-2">
              <div className="text-xs text-blue-900 mb-2 flex items-center gap-1">
                <Link2 className="size-3" />
                Can reuse {ci.reuseOpportunities.length} answer{ci.reuseOpportunities.length !== 1 ? 's' : ''}:
              </div>
              <div className="space-y-2">
                {ci.reuseOpportunities.map((opp, i) => (
                  <div key={i} className="bg-white border border-blue-200 rounded p-2">
                    <div className="text-xs text-gray-700 mb-1">
                      <strong>Q:</strong> {opp.question}
                    </div>
                    <div className="text-xs text-gray-600 mb-2">
                      <strong>From {opp.fromCI}:</strong> {typeof opp.answer === 'string' ? opp.answer : JSON.stringify(opp.answer)}
                    </div>
                    <button
                      onClick={() => onApplyAnswer(opp.questionId, ci.id, opp.answer)}
                      disabled={applying}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <Copy className="size-3" />
                      Apply This Answer
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ci.reuseOpportunities.length === 0 && ci.missingCritical.length === 0 && (
            <div className="text-xs text-gray-600">
              No action items. {ci.buildReadiness >= 80 ? 'Ready to build.' : 'Continue answering questions.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}